/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/display-name */
import { Button, Select, Table } from "antd";
import React, {
    CSSProperties,
    ReactElement,
    useCallback,
    useEffect, useRef, useState
} from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import {
    setFvpMapMarkers,
    setSelectedFVP
} from "../../../../Actions/GPSInsightsActions";
import { featureFlags } from "../../../../Common/CurrentFeatureFlags";
import { DigitalShareAppType } from "../../../../Enums/DigitalShareAppType";
import { DigitalShareItemType } from "../../../../Enums/DigitalShareItemType";
import { RenderType } from "../../../../Enums/RenderType";
import {
    colorTwoBright,
    colorTwoDark,
    deviceWidth
} from "../../../../globalStyles";
import { IBrandCategory } from "../../../../Models/IBrandCategory";
import { IMapMarker, MapMarker } from "../../../../Models/IMapMarker";
import {
    IFrequentlyVisitedPlace,
    IGCPBigQueryJob,
    IPoint
} from "../../../../Models/Models";
import {
    gpsFVPDataSelector,
    gpsPolygonNames
} from "../../../../Reducers/Selectors/GPSInsightsSelectors";
import {
    ReportCategoryHeader,
    ReportCategoryText, ReportH4,
    ReportH5, useWindowResize
} from "../../../Common";
import {
    isOutputSelected,
    registerElement,
    registerRef
} from "../../../Common/DigitalShare/DigitalShareGenerator";
import FrequentlyVisitedPlacesMap from "./FrequentlyVisitedPlacesMap";
import PolygonCircleWithNumber from "./PolygonCircleWithNumber";


enum TimeElement {
    Anytime,
    Before,
    After,
}
enum TableOption {
    One,
    Two,
}
type TimeOption = { name: string; value: TimeElement };
interface FrequentlyVisitedPlacesProps {
    fvpData: IFrequentlyVisitedPlace[][];
    renderType?: RenderType;
    app?: DigitalShareAppType;
    itemType?: DigitalShareItemType;
    onMapReady?: () => void;
}
interface IMapLegendItemProps {
    backgroundColor: string;
}
const MapLegendItem = styled.div`
  cursor: pointer;
  background-color: ${(props: IMapLegendItemProps) => props.backgroundColor};
  height: 14px;
  width: 14px;
  border-radius: 50%;
`;
const MapLegendWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 17px;
`;
const DropdownWrapper = styled.div`
  width: 33%;
  max-width: 300px;
`;
const dropdownStyle: CSSProperties = {
    width: "100%",
};

const StyledTable = styled(Table)`
    .ant-table-thead, .ant-table-tbody {
        > tr {
            > td {
                font-family: Manrope;
                font-size: 12px;
                padding: 4px 0;
            }
        }
        > tr {
            > th {
                font-family: Manrope;
                font-size: 12px;
                padding: 7px 0;
            }
        }
    }
`;

const googlePushpinImage
    = "https://maps.gstatic.com/mapfiles/api-3/images/spotlight-poi2.png";

export const FrequentlyVisitedPlaces = (
    props: FrequentlyVisitedPlacesProps
): JSX.Element => {

    /** misc */
    const dispatch = useDispatch();
    const windowSize = useWindowResize();
    const maxNumLocations = 5;
    /** store */
    const store = useSelector(gpsFVPDataSelector);
    const polygonNames = useSelector(gpsPolygonNames);
    /** state */
    const [selectedCategory, setSelectedCategory]
        = useState<IBrandCategory | null>(null);
    const [selectedSubCategory, setSelectedSubCategory]
        = useState<IBrandCategory | null>(null);
    const [selectedTimeElement, setSelectedTimeElement] = useState<TimeElement>(
        TimeElement.Anytime
    );
    const [polygons, setPolygons] = useState<IPoint[][]>([]);
    const [mapReady, setMapReady] = useState<boolean>(false);

    /** effects */
    useEffect(() => {
        /** the markers for the frequently visited places */
        const markers: IMapMarker[] = getMapMarkers(maxNumLocations);
        dispatch(setFvpMapMarkers(markers));
    }, [store.jobs, selectedCategory, selectedSubCategory, selectedTimeElement]);

    useEffect(() => {
        /** only get sub categories that have results in the data */
        const filteredSubCategories: IBrandCategory[] = getFilteredSubCategories();
        /** always select the first category to make it obvious to the user
     * where the data in the table / map is coming from
     */
        setSelectedSubCategory(filteredSubCategories[0]);
    }, [selectedCategory]);

    useEffect(() => {
        const polys = getMapPolygons();
        setPolygons(polys);
    }, [store.jobs]);

    /** Failsafe for "mapReady" state if the Google map fails to load */
    useEffect(() => {
        const onMapReadyFailsafe = setTimeout(() => setMapReady(true), 10000);
        return () => clearTimeout(onMapReadyFailsafe);
    }, []);

    /** misc */
    const timeElements: TimeOption[] = [
        {
            name: "Anytime",
            value: TimeElement.Anytime,
        },
        {
            name: "Before",
            value: TimeElement.Before,
        },
        {
            name: "After",
            value: TimeElement.After,
        },
    ];

    const getMapPolygons = (): IPoint[][] => {
        /** the markers for the job polygon centroid */
        const rings: IPoint[][] = [];
        for (const job of store.jobs) {
            for (const ring of job.jobPolygon.linearRings) {
                rings.push(ring);
            }
        }
        return rings;
    };

    const getFilteredPrimaryCategories = (): IBrandCategory[] => {
        /** only add categories that match up with a brand. Otherwise
         * it becomes a treasure hunt to find a category that has results
         * in it.
         */
        const filteredPrimaryCategories: IBrandCategory[] = [];

        for (const data of props.fvpData) {
            for (const fvp of data) {
                const foundCategory: IBrandCategory = store.categories.find(
                    (cat: IBrandCategory) => {
                        return cat.naicsCode === fvp.categoryId;
                    }
                );
                if (foundCategory) {
                    const categoryExists = filteredPrimaryCategories.find(
                        (cat: IBrandCategory) => {
                            return cat.categoryName === foundCategory.categoryName;
                        }
                    );
                    if (!categoryExists) {
                        filteredPrimaryCategories.push(foundCategory);
                    }
                }
            }
        }
        /** sort alphabetically */
        return filteredPrimaryCategories.sort(
            (a: IBrandCategory, b: IBrandCategory) => {
                if (a.categoryName < b.categoryName) return -1;
                if (a.categoryName > b.categoryName) return 1;
                return 0;
            }
        );
    };

    const getFilteredSubCategories = (): IBrandCategory[] => {
        /** only add sub-categories that match up with a brand for the same
         * reason as stated above
         */
        const filteredSubCategories: IBrandCategory[] = [];

        if (selectedCategory) {
            const subCatsInCategory: IBrandCategory[] = store.categories.filter(
                (cat: IBrandCategory) => {
                    return cat.categoryName === selectedCategory.categoryName;
                }
            );

            for (const data of props.fvpData) {
                for (const fvp of data) {
                    const foundSubCat: IBrandCategory = subCatsInCategory.find(
                        (cat: IBrandCategory) => {
                            return cat.naicsCode === fvp.categoryId;
                        }
                    );
                    if (foundSubCat) {
                        const subCatExists = filteredSubCategories.find(
                            (cat: IBrandCategory) => {
                                return cat.subcategoryName === foundSubCat.subcategoryName;
                            }
                        );
                        if (!subCatExists) {
                            filteredSubCategories.push(foundSubCat);
                        }
                    }
                }
            }
        }
        /** sort alphabetically */
        return filteredSubCategories.sort(
            (a: IBrandCategory, b: IBrandCategory) => {
                if (a.subcategoryName < b.subcategoryName) return -1;
                if (a.subcategoryName > b.subcategoryName) return 1;
                return 0;
            }
        );
    };

    const getHeaderAddressVerbiage = (): JSX.Element[] => {
        let key = 0;
        const verbiage: JSX.Element[] = [
            <b key={key}>{store.jobs[0].jobPolygon.name}</b>,
        ];
        if (store.jobs.length > 1) {
            verbiage.push(
                <span key={++key}>
                    {" "}
                    and <b>{store.jobs[1].jobPolygon.name}</b>
                </span>
            );
        }

        return verbiage;
    };

    const getTopLocations = (
        numLocations: number,
        jobId: string
    ): IFrequentlyVisitedPlace[] => {
        /** the data for only this job */
        let fvps: IFrequentlyVisitedPlace[] = [];
        for (const data of props.fvpData) {
            const total = getFvpTotal(jobId);
            for (const fvp of data) {
                /** 11709 - don't show items with a percentage of 0 */
                const p = getFvpPercentage(fvp, total) * 100;
                if (fvp.jobId === jobId && p > 0) {
                    fvp.percentage = p;
                    fvps.push(fvp);
                }
            }
        }
        /** sort the data depending on what is selected in the dropdowns */
        if (selectedSubCategory) {
            /** subcategory is more specific, so filter by this over category if possible */
            fvps = fvps.filter((f: IFrequentlyVisitedPlace) => {
                return f.categoryId === selectedSubCategory.naicsCode;
            });
        } else if (selectedCategory) {
            /** only a category is chosen, filter by this only */
            fvps = fvps.filter((f: IFrequentlyVisitedPlace) => {
                return f.categoryId === selectedCategory.naicsCode;
            });
        }
        if (selectedTimeElement !== null) {
            switch (selectedTimeElement) {
                case TimeElement.Anytime: {
                    fvps = fvps.filter((f: IFrequentlyVisitedPlace) => {
                        return (
                            f.categoryId.toUpperCase() !== "HOME"
                            && f.categoryId.toUpperCase() !== "WORK"
                        );
                    });
                    fvps.sort(
                        (a: IFrequentlyVisitedPlace, b: IFrequentlyVisitedPlace) => {
                            if (a.visitCount < b.visitCount) return 1;
                            if (a.visitCount > b.visitCount) return -1;
                            return 0;
                        }
                    );
                    break;
                }
                case TimeElement.Before: {
                    fvps.sort(
                        (a: IFrequentlyVisitedPlace, b: IFrequentlyVisitedPlace) => {
                            if (a.beforeVisitCount < b.beforeVisitCount) return 1;
                            if (a.beforeVisitCount > b.beforeVisitCount) return -1;
                            return 0;
                        }
                    );
                    break;
                }
                case TimeElement.After: {
                    fvps.sort(
                        (a: IFrequentlyVisitedPlace, b: IFrequentlyVisitedPlace) => {
                            if (a.afterVisitCount < b.afterVisitCount) return 1;
                            if (a.afterVisitCount > b.afterVisitCount) return -1;
                            return 0;
                        }
                    );
                    break;
                }
            }
        }

        return fvps.slice(0, numLocations);
    };

    const categoryChange = (catName: string): void => {
        const category: IBrandCategory = getFilteredPrimaryCategories().find(
            (c: IBrandCategory) => {
                return c.categoryName === catName;
            }
        );
        setSelectedCategory(category);
    };

    const subCategoryChange = (catName: string): void => {
        const category: IBrandCategory = getFilteredSubCategories().find(
            (c: IBrandCategory) => {
                return c.subcategoryName === catName;
            }
        );
        setSelectedSubCategory(category);
        setSelectedTimeElement(TimeElement.Anytime);
    };

    const timeChange = (timeElement: TimeElement): void => {
        setSelectedTimeElement(timeElement);
    };

    const filterInput = (input: string, option: ReactElement) => {
        return option.props.children
            .toString()
            .toLowerCase()
            .includes(input.toLowerCase());
    };

    const selectTopLocation = (place: IFrequentlyVisitedPlace): void => {
        const markersCopy: IMapMarker[] = [...store.mapMarkers].map(
            (m: IMapMarker) => {
                return {
                    ...m,
                    isSelected: place.storeId === m.id,
                };
            }
        );

        dispatch(setFvpMapMarkers(markersCopy));
        dispatch(setSelectedFVP(place));
    };

    const getMapMarkers = (numMarkersToGet: number): IMapMarker[] => {
        const markers: IMapMarker[] = [];
        store.jobs.forEach((job: IGCPBigQueryJob, jobIndex: number) => {
            const topLocations: IFrequentlyVisitedPlace[] = getTopLocations(
                numMarkersToGet,
                job.jobId
            );
            topLocations.map(
                (place: IFrequentlyVisitedPlace, locationIndex: number) => {
                    if (place.latitude !== 0 || place.longitude !== 0) {
                        const marker = new MapMarker(
                            `${place.storeId}`,
                            place.latitude,
                            place.longitude
                        );
                        if (place.brand) {
                            marker.icon = place.brand.logoPath;
                        }
                        marker.name = place.name.charAt(0);
                        marker.rank = locationIndex + 1;
                        marker.content = (
                            <div
                                style={{
                                    height: "18px",
                                    width: "18px",
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: "bold",
                                    color: "#fff",
                                    backgroundColor: jobIndex === 0 ? "#1ce180" : "#2b8b49",
                                }}
                            >
                                {marker.rank}
                            </div>
                        );
                        markers.push(marker);
                    }
                }
            );
        });
        return markers;
    };

    const getFvpPercentage = (
        place: IFrequentlyVisitedPlace,
        total: number
    ): number => {
        switch (selectedTimeElement) {
            case TimeElement.Anytime: {
                return place.visitCount / total;
            }
            case TimeElement.Before: {
                return place.beforeVisitCount / total;
            }
            case TimeElement.After: {
                return place.afterVisitCount / total;
            }
            default: {
                return 0;
            }
        }
    };

    const getFvpTotal = (jobId: string): number => {
        let runningTotal = 0;

        for (const data of props.fvpData) {
            for (const fvp of data) {
                if (fvp.jobId === jobId) {
                    switch (selectedTimeElement) {
                        case TimeElement.Anytime: {
                            runningTotal += fvp.visitCount;
                            break;
                        }
                        case TimeElement.Before: {
                            runningTotal += fvp.beforeVisitCount;
                            break;
                        }
                        case TimeElement.After: {
                            runningTotal += fvp.afterVisitCount;
                            break;
                        }
                        default: {
                            //Should never hit this
                            return 0;
                        }
                    }
                }
            }
        }

        return runningTotal;
    };

    const getTableColumns = (tableOption: TableOption, polygonColor: string) => [
        {
            title: "Location",
            dataIndex: null,
            render: (text: any, place: IFrequentlyVisitedPlace, index: number) => {
                return (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <div
                            style={{
                                fontWeight: "bold",
                                fontSize: "20px",
                                marginRight: "20px",
                            }}
                        >
                            {
                                <PolygonCircleWithNumber
                                    color={polygonColor}
                                    numberToDisplay={index + 1}
                                />
                            }
                        </div>

                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <div style={{ fontWeight: "bold" }}>{place.name}</div>
                            {place.address && place.city && place.state && place.zipcode && (
                                <div>
                                    {place.address}, {place.city} {place.state} {place.zipcode}
                                </div>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            title: "Visitor %",
            dataIndex: null,
            defaultSortOrder: "descend" as any,
            width: 115,
            render: (place: IFrequentlyVisitedPlace) => {
                return <span>{place.percentage.toLocaleString()}</span>;
            },
        },
        {
            title: "View",
            dataIndex: null,
            width: 100,
            render: (place: IFrequentlyVisitedPlace) => {
                return (
                    (place.latitude !== 0 || place.longitude !== 0) && (
                        <Button onClick={() => selectTopLocation(place)}>Map View</Button>
                    )
                );
            },
        },
    ];

    const getShareTableColumns = (tableOption: TableOption, polygonColor: string) => [
        {
            title: "Rank",
            dataIndex: null,
            width: 50,
            render: (text: any, place: IFrequentlyVisitedPlace, index: number) => {
                return <div>{index + 1}</div>;
            },
        },
        {
            title: "Name",
            dataIndex: null,
            width: 225,
            render: (text: any, place: IFrequentlyVisitedPlace, index: number) => {
                return <div>{place.name}</div>;
            },
        },
        {
            title: "Address",
            dataIndex: null,
            render: (text: any, place: IFrequentlyVisitedPlace, index: number) => {
                return <div>{place.address}</div>;
            },
        },
        {
            title: "City",
            dataIndex: null,
            width: 115,
            render: (text: any, place: IFrequentlyVisitedPlace, index: number) => {
                return <div>{place.city}</div>;
            },
        },
        {
            title: "State",
            dataIndex: null,
            width: 50,
            render: (text: any, place: IFrequentlyVisitedPlace, index: number) => {
                return <div>{place.state}</div>;
            },
        },
        {
            title: "Zip",
            dataIndex: null,
            width: 65,
            render: (text: any, place: IFrequentlyVisitedPlace, index: number) => {
                return <div>{place.zipcode}</div>;
            },
        },
        {
            title: "Visitor %",
            dataIndex: null,
            width: 65,
            defaultSortOrder: "descend" as any,
            render: (place: IFrequentlyVisitedPlace) => {
                return <div>{place.percentage.toLocaleString()}</div>;
            },
        },
    ];

    const handleMarkerClick = (m: IMapMarker, jobId: string) => {
        const markersCopy: IMapMarker[] = [...store.mapMarkers].map(
            (mark: IMapMarker) => {
                return {
                    ...mark,
                    isSelected: mark.id === m.id,
                };
            }
        );

        const foundPlace = getTopLocations(maxNumLocations, jobId).find(
            (fvp: IFrequentlyVisitedPlace) => {
                return fvp.storeId === m.id;
            }
        );

        dispatch(setFvpMapMarkers(markersCopy));
        dispatch(setSelectedFVP(foundPlace));
    };

    const getTableDisplayStyle = (): CSSProperties => {
        const displayObj: CSSProperties = {
            display: store.jobs.length >= 1 ? "flex" : "initial",
        };
        if (windowSize.width < deviceWidth.desktop) {
            displayObj.flexDirection = "column";
        }
        return displayObj;
    };

    const handleReset = (): void => {
        const markersCopy: IMapMarker[] = [...store.mapMarkers].map(
            (m: IMapMarker) => {
                return {
                    ...m,
                    isSelected: false,
                };
            }
        );
        setSelectedSubCategory(null);
        setSelectedCategory(null);
        setSelectedTimeElement(TimeElement.Anytime);
        dispatch(setFvpMapMarkers(markersCopy));
        dispatch(setSelectedFVP(null));
    };

    const getmapLegend = (): JSX.Element[] => {
        const elements: JSX.Element[] = [];
        store.jobs.forEach((job: IGCPBigQueryJob, index: number) => {
            elements.push(
                <div key={job.id} style={{ display: "flex", margin: "0 12px" }}>
                    <MapLegendItem
                        backgroundColor={index === 0 ? "#1ce180" : "#2b8b49"}
                    />
                    <b style={{ marginLeft: "3px" }}>{job.jobPolygon.name}</b>
                </div>
            );
        });
        return elements;
    };

    const onMapReady = () => {
        setMapReady(true);

        if (props.onMapReady) {
            props.onMapReady();
        }
    };

    const ref = useRef(null);

    const fvpMap = <div>
        <FrequentlyVisitedPlacesMap
            containerElement={<div style={{ height: `576px` }} />}
            mapElement={<div style={{ height: `100%` }} />}
            markers={store.mapMarkers}
            polygons={polygons}
            onMapReady={onMapReady}
            renderType={props.renderType}
        />
    </div>;

    const chart = (
        <div>
            <div
                style={{
                    width: "100%",
                    display: "inline-flex",
                    padding: "10px 15% 20px",
                }}
            >
                <div style={{ width: "100%" }}>
                    <ReportCategoryHeader>Category</ReportCategoryHeader>
                    <ReportCategoryText>
                        {selectedCategory?.categoryName
                            ? selectedCategory?.categoryName
                            : "No Selection"}
                    </ReportCategoryText>
                </div>
                <div style={{ width: "100%", margin: "0px 15px" }}>
                    <ReportCategoryHeader>Subcategory</ReportCategoryHeader>
                    <ReportCategoryText>
                        {selectedSubCategory?.subcategoryName
                            ? selectedSubCategory?.subcategoryName
                            : "No Selection"}
                    </ReportCategoryText>
                </div>
                <div style={{ width: "100%" }}>
                    <ReportCategoryHeader>Time Range</ReportCategoryHeader>
                    <ReportCategoryText>
                        {timeElements[selectedTimeElement]?.name}
                    </ReportCategoryText>
                </div>
            </div>
            <div style={{ width: "100%", marginBottom: "20px" }}>
                <img
                    src={googlePushpinImage}
                    height={16}
                    style={{ margin: "0 9px 5px 0" }}
                />
                <ReportH5>{store.jobs[0].jobPolygon.name}</ReportH5>
                <StyledTable
                    columns={getShareTableColumns(TableOption.One, colorTwoBright)}
                    dataSource={getTopLocations(maxNumLocations, store.jobs[0].jobId)}
                    loading={store.loadingJobs}
                    pagination={false}
                    rowKey={(place: IFrequentlyVisitedPlace) =>
                        place.storeId + place.name
                    }
                />
            </div>

            {store.jobs[1] && (
                <>
                    <div style={{ width: "100%" }}>
                        <img
                            src={googlePushpinImage}
                            height={16}
                            style={{ margin: "0 9px 5px 0" }}
                        />
                        <ReportH5>{store.jobs[1].jobPolygon.name}</ReportH5>
                        <StyledTable
                            columns={getShareTableColumns(TableOption.Two, colorTwoDark)}
                            dataSource={getTopLocations(maxNumLocations, store.jobs[1].jobId)}
                            loading={store.loadingJobs}
                            pagination={false}
                            rowKey={(place: IFrequentlyVisitedPlace) =>
                                place.storeId + place.name
                            }
                        />
                    </div>
                </>
            )}
        </div>
    );

    if (
        featureFlags.DigitalShare
        && props.renderType === RenderType.DIGITAL_SHARE
        && isOutputSelected(props.app, props.itemType)
    ) {
        const chartText = `Shows top 5 places that visitors to ${polygonNames[0]}${polygonNames.length > 1 && " and " + polygonNames[1]} visited during the specified time frame.`;
        
        registerRef(props.app.toString(), props.itemType.toString(), ref);
        registerElement(
            props.app.toString(),
            props.itemType.toString(),
            chartText,
            chart
        );

        // JA - Couldn't get this to work inside FrequentlyVisitedPlacesMap.tsx
        registerElement(
            DigitalShareAppType.Mobilytics,
            DigitalShareItemType.FrequentlyVisitedPlacesMap,
            "",
            fvpMap
        );
    }

    return (
        <div className="fvp-container">
            {
                mapReady &&
                <>
                    <ReportH4>Frequently Visited Places</ReportH4>

                    <div
                        style={{
                            marginBottom: "47px",
                            lineHeight: "26px",
                        }}
                    >
                        Shows top 5 places that visitors to {...getHeaderAddressVerbiage()}{" "}
                        visited during the specified time frame.
                    </div>

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            marginBottom: "39px",
                        }}
                    >
                        <DropdownWrapper
                            style={{
                                marginRight: "10px",
                            }}
                        >
                            {/* <div style={{
                                fontWeight: "bold",
                                marginBottom: "4px"
                            }}>Category</div> */}
                            <Select
                                placeholder="Choose a category"
                                onChange={categoryChange}
                                loading={false}
                                disabled={false}
                                showSearch={true}
                                filterOption={filterInput}
                                allowClear={true}
                                value={selectedCategory?.categoryName}
                                style={dropdownStyle}
                            >
                                {getFilteredPrimaryCategories().map((cat: IBrandCategory) => {
                                    return (
                                        <Select.Option value={cat.categoryName} key={cat.categoryName}>
                                            {cat.categoryName}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        </DropdownWrapper>
                        <DropdownWrapper
                            style={{
                                margin: "0 10px",
                            }}
                        >
                            {/* <div style={{
                                fontWeight: "bold",
                                marginBottom: "4px"
                            }}>Sub-Category</div> */}
                            <Select
                                placeholder="Choose a sub category"
                                onChange={subCategoryChange}
                                loading={false}
                                disabled={!selectedCategory}
                                showSearch={true}
                                filterOption={filterInput}
                                value={selectedSubCategory?.subcategoryName}
                                style={dropdownStyle}
                            >
                                {getFilteredSubCategories().map((cat: IBrandCategory) => {
                                    return (
                                        <Select.Option
                                            value={cat.subcategoryName}
                                            key={cat.subcategoryName}
                                        >
                                            {cat.subcategoryName}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        </DropdownWrapper>
                        <DropdownWrapper
                            style={{
                                marginLeft: "10px",
                            }}
                        >
                            {/* <div style={{
                                fontWeight: "bold",
                                marginBottom: "4px"
                            }}>Time Range</div> */}
                            <Select
                                placeholder="Choose a time element"
                                onChange={timeChange}
                                loading={false}
                                disabled={false}
                                showSearch={true}
                                filterOption={filterInput}
                                value={selectedTimeElement}
                                style={dropdownStyle}
                            >
                                {timeElements.map((t: TimeOption) => {
                                    return (
                                        <Select.Option value={t.value} key={t.name}>
                                            {t.name}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        </DropdownWrapper>
                    </div>
                    <div style={getTableDisplayStyle()}>
                        <div style={{ width: "100%" }}>
                            <img
                                src={googlePushpinImage}
                                height={30}
                                style={{ margin: "0 9px 10px 0" }}
                            />
                            <ReportH4>{store.jobs[0].jobPolygon.name}</ReportH4>
                            <Table
                                columns={getTableColumns(TableOption.One, colorTwoBright)}
                                dataSource={getTopLocations(maxNumLocations, store.jobs[0].jobId)}
                                loading={store.loadingJobs}
                                pagination={false}
                                rowKey={(place: IFrequentlyVisitedPlace) =>
                                    place.storeId + place.name
                                }
                                rowClassName={(place: IFrequentlyVisitedPlace, index: number) =>
                                    place.storeId === store.selectedFVP?.storeId ? "selected" : ""
                                }
                                style={{
                                    marginBottom: "26px",
                                    width: "100%",
                                }}
                            />
                        </div>

                        {store.jobs[1] && (
                            <>
                                <div
                                    style={{
                                        border: "1px solid #e9e9e9",
                                        margin: "14px",
                                    }}
                                ></div>
                                <div style={{ width: "100%" }}>
                                    <img
                                        src={googlePushpinImage}
                                        height={30}
                                        style={{ margin: "0 9px 10px 0" }}
                                    />
                                    <ReportH4>{store.jobs[1].jobPolygon.name}</ReportH4>
                                    <Table
                                        columns={getTableColumns(TableOption.Two, colorTwoDark)}
                                        dataSource={getTopLocations(
                                            maxNumLocations,
                                            store.jobs[1].jobId
                                        )}
                                        loading={store.loadingJobs}
                                        pagination={false}
                                        rowKey={(place: IFrequentlyVisitedPlace) =>
                                            place.storeId + place.name
                                        }
                                        rowClassName={(place: IFrequentlyVisitedPlace, index: number) =>
                                            place.storeId === store.selectedFVP?.storeId ? "selected" : ""
                                        }
                                        style={{
                                            marginBottom: "26px",
                                            width: "100%",
                                        }}
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    <div style={{ position: "relative" }}>
                        <Button
                            onClick={handleReset}
                            style={{
                                position: "absolute",
                                top: "10px",
                                left: "10px",
                                zIndex: 1,
                            }}
                        >
                            Reset
                        </Button>
                    </div>
                </>
            }

            {fvpMap}

            <MapLegendWrapper>{getmapLegend()}</MapLegendWrapper>
        </div>
    );
};
